import { func, object, string } from 'prop-types';
import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { FONT_SIZES, valuesToOptions } from 'hb-builder/utils/fonts';

const FontSize = ({
  defaultAttributes,
  item,
  onChange,
  settingKey,
  settingLineHeightKey,
  title,
  includeLineHeight = true,
  forceUpdateAllChildren = false,
}) => {
  const initialValue =
    item.attributes[settingKey] || defaultAttributes[settingKey];

  const [value, setValue] = useState(
    initialValue?.toString()?.includes('rem')
      ? convertRemToPx(initialValue)
      : initialValue,
  );

  const options = valuesToOptions(FONT_SIZES, (value) => `${value}px`);

  const theme = useTheme();

  useEffect(() => {
    const initialValue =
      item.attributes[settingKey] || defaultAttributes[settingKey];

    if (!value && initialValue) {
      setValue(initialValue);
    }
  }, [defaultAttributes, item.attributes, settingKey, value]);

  const handleChange = (e) => {
    const newValue = e.value;

    setValue(newValue);

    const lineHeightValue = Math.round(
      parseInt(newValue, 10) * theme.builder.typography.lineHeight,
    );

    onChange({
      id: item.id,
      forceUpdateAllChildren,
      values: [
        {
          settingKey: `attributes.${settingKey}`,
          value: parseInt(newValue, 10),
        },
        includeLineHeight && {
          settingKey: `attributes.${settingLineHeightKey}`,
          value: lineHeightValue,
        },
      ].filter(Boolean),
    });
  };

  const formattedValue = getReactSelectValue(options, value);

  return (
    <FontSizeWrapper>
      <InputTitle id="settingsFontSize-input" label={title} position="left" />
      <ReactSelectInput
        dataTestId="settingsFontSize"
        id="settingsFontSize"
        name="settingsFontSize"
        onChange={handleChange}
        options={options}
        placeholder={formattedValue?.label}
        value={formattedValue}
        variant="small"
        width={155}
      />
    </FontSizeWrapper>
  );
};

export default FontSize;

const FontSizeWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 4,
}))``;

FontSize.defaultProps = {
  title: __('Font size'),
};

FontSize.propTypes = {
  defaultAttributes: object,
  item: object.isRequired,
  onChange: func.isRequired,
  settingKey: string.isRequired,
  settingLineHeightKey: string.isRequired,
  title: string.isRequired,
};
